import { z } from "zod";
const identity = z.string().min(1).max(256).regex(/^[^\r\n]+$/u);
const modalEndpoint = z.string().url().refine((raw) => {
    const url = new URL(raw);
    return url.protocol === "https:" && url.hostname.endsWith(".modal.run") &&
        /^\/v1\/?$/u.test(url.pathname) && !url.username && !url.password &&
        !url.search && !url.hash;
});
/** One operator-owned deployment binding for the existing native parent route. */
export const VenusParentBindingSchema = z.object({
    contract: z.literal("mlx.venus.parent-binding.v1"),
    bindingId: identity,
    phase: z.enum(["open", "closed"]),
    expiresAt: z.string().datetime(),
    deploymentTier: z.literal("maintenance"),
    modalEnvironment: identity,
    modalApp: identity,
    baseUrl: modalEndpoint,
    servedModelId: identity,
    displayName: identity,
    checkpoint: z.string().regex(/^[\w.-]+\/[\w.-]+$/u),
    revision: z.string().regex(/^[a-f0-9]{40}$/u),
    templateHash: z.string().regex(/^[a-f0-9]{64}$/u),
    contextWindowTokens: z.number().int().min(16_384),
    chatTemplateKwargs: z.union([
        z.object({ enable_thinking: z.literal(true), preserve_thinking: z.literal(true) }).strict(),
        z.object({ clear_thinking: z.literal(false) }).strict(),
    ]),
}).strict();
export function assertVenusParentBindingOpen(binding, now = Date.now()) {
    if (binding.phase !== "open" || Date.parse(binding.expiresAt) <= now) {
        throw new Error("Venus parent binding is closed or expired");
    }
}
/** The caller supplies server-side file I/O; this shared contract never imports fs. */
export function readVenusParentBinding(env, readText, now = Date.now()) {
    const path = env.MLX_VENUS_PARENT_BINDING_FILE?.trim();
    if (!path)
        return undefined;
    if (!path.startsWith("/"))
        throw new Error("Venus parent binding file must be absolute");
    let value;
    try {
        value = JSON.parse(readText(path));
    }
    catch {
        throw new Error("Venus parent binding file is unreadable or invalid JSON");
    }
    const result = VenusParentBindingSchema.safeParse(value);
    if (!result.success)
        throw new Error("Venus parent binding schema is invalid");
    const binding = result.data;
    assertVenusParentBindingOpen(binding, now);
    const endpoint = new URL(binding.baseUrl).toString().replace(/\/+$/u, "");
    for (const key of ["MLX_VENUS_MODAL_BASE_URL", "MLX_VENUS_EXPLORER_MODAL_BASE_URL",
        "MLX_VENUS_QUASAR_MODAL_BASE_URL"]) {
        const configured = env[key]?.trim();
        if (configured && new URL(configured).toString().replace(/\/+$/u, "") === endpoint) {
            throw new Error("Venus parent maintenance endpoint must be isolated");
        }
    }
    return binding;
}
export function venusParentBindingApiKey(env) {
    const apiKey = env.MLX_VENUS_PARENT_BINDING_API_KEY?.trim();
    if (!apiKey)
        throw new Error("Venus parent binding requires its separate API credential");
    if (["MLX_VENUS_MODAL_API_KEY", "MLX_VENUS_EXPLORER_MODAL_API_KEY", "MLX_VENUS_QUASAR_MODAL_API_KEY"]
        .some((key) => env[key]?.trim() === apiKey)) {
        throw new Error("Venus parent binding credential must be isolated");
    }
    return apiKey;
}
